import { bool, node, number, string } from "prop-types";
import { forwardRef } from "react";

import styled, { css } from "styled-components/macro";
import { glow } from "theme";

import FnrShineButton from "UI/FnrButton3/FnrShineButton";
import { colors, marketingColors } from "UI/colors";

import { addOpacity } from "Utilities/Colors/colorUtils";
import dynBackgroundColor from "Utilities/interpolations/dynBackgroundColor";
import flash from "Utilities/interpolations/flash";

// The sites drawing this look all thin their accent by the same amount before
// filling with it — a solid fill is hard on the label's contrast.
// activeBackgroundColor is the way out for one that really does want a solid.
const TINT_OPACITY = 0.2;

/**
 * @typedef {object} FnrAccentShineButtonProps
 * @property {React.ReactNode} [children] - goes inside AccentShineLabel
 * @property {boolean} [active] - light it up from React state
 * @property {boolean} [activateOnHover] - also light up on :hover/:focus-visible
 * @property {string} [accent] - colours the glow ring, and the fill by default
 * @property {string} [activeBackgroundColor] - fill only, when it isn't the accent
 * @property {string} [shineColor] - the shine, which the accent does NOT set
 * @property {string} [restBackgroundColor] - what it sits at unlit
 * @property {boolean} [isFlashing]
 * @property {string} [flashToColor]
 * @property {number} [borderRadius]
 */

/**
 * An FnrShineButton that's nothing at rest and fills with a tint of its accent
 * behind a 1px glow ring when it's the thing you're on. The NavBar draws its
 * buttons this way and the change log's back pill does too.
 *
 * Two ways to light it up, and they compose:
 * - `active`, for the buttons that read React state — a popover is open, a
 *   route is current.
 * - `activateOnHover`, for the ones with no state to read, where being pointed
 *   at is the whole of it.
 *
 * Children land in AccentShineLabel, and that isn't a convenience.
 * FnrShineButton puts three boxes between itself and its children (the shine
 * layer, ButtonContent, then the loading bricks), so a flex row set on the
 * button never reaches them — the icon and the label come out stacked. The
 * label is that row. Style it through `${AccentShineLabel}` from a
 * styled(FnrAccentShineButton) if it needs a gap or padding.
 *
 * Not to be confused with FnrDemotedShineButton, which is a different look
 * (taupe shine, fnBlue accent text, no glow) and shares nothing with this but
 * the inner-label trick.
 *
 * @type {React.ForwardRefExoticComponent<FnrAccentShineButtonProps & React.RefAttributes<HTMLButtonElement>>}
 */
const FnrAccentShineButton = forwardRef(
  (
    {
      children,
      active = false,
      activateOnHover = false,
      accent = marketingColors.blue,
      activeBackgroundColor,
      restBackgroundColor = colors.transparent,
      isFlashing = false,
      flashToColor,
      borderRadius = 24,
      className,
      as,
      ...rest
    },
    ref
  ) => {
    const lit = activeBackgroundColor || addOpacity(accent, TINT_OPACITY);
    return (
      <SFnrShineButton
        ref={ref}
        className={className}
        /* forwardedAs, never as: this is a styled(), so an `as` lands on the
           styled wrapper and swaps FnrShineButton out for the Link entirely —
           no shine, no ring, and every prop here spilling onto the anchor.
           forwardedAs hands it down as a plain prop instead, and FnrShineButton
           is the one that knows what to do with it. */
        forwardedAs={as}
        borderRadius={borderRadius}
        $active={active}
        $activateOnHover={activateOnHover}
        $glowColor={accent}
        $litBackgroundColor={lit}
        // flash animates from this one, so it has to be the resolved colour and
        // not just the rest colour
        $backgroundColor={active ? lit : restBackgroundColor}
        $isFlashing={isFlashing}
        $flashToColor={flashToColor}
        {...rest}
      >
        <AccentShineLabel>{children}</AccentShineLabel>
      </SFnrShineButton>
    );
  }
);

FnrAccentShineButton.propTypes = {
  children: node,
  active: bool,
  activateOnHover: bool,
  accent: string,
  activeBackgroundColor: string,
  shineColor: string,
  restBackgroundColor: string,
  isFlashing: bool,
  flashToColor: string,
  borderRadius: number,
  className: string,
};

export const AccentShineLabel = styled.span`
  display: flex;
  align-items: center;
`;

const litRing = css`
  background-color: ${({ $litBackgroundColor }) => $litBackgroundColor};
  ${glow}
`;

/* box-shadow: none rather than glow's own $noGlow, which emits
   "box-shadow 0 0 0 0 #ffffff" — no colon, so the browser drops the whole
   declaration. Same result, but only one of the two says so. */
const SFnrShineButton = styled(FnrShineButton)`
  ${dynBackgroundColor}
  ${({ $active }) => ($active ? glow : "box-shadow: none;")}
  ${flash}
  transition: background-color 300ms ease-in-out, box-shadow 300ms ease-in-out;

  ${({ $activateOnHover }) =>
    $activateOnHover &&
    css`
      &:hover,
      &:focus-visible {
        ${litRing}
      }
    `}
`;

export default FnrAccentShineButton;
